import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';
import {
  searchProducts,
  type ProductSearchRow,
} from '../lib/productSearch';
import {
  MANUAL_ORDER_MAX_LINES,
  type SelectedProductRow,
} from '../types/manualOrder';

interface ProductSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 이미 선택된 행(편집 시작값). */
  initialSelection: SelectedProductRow[];
  onApply: (rows: SelectedProductRow[]) => void;
}

const toSelectedRow = (p: ProductSearchRow): SelectedProductRow => ({
  productId: p.productId,
  variantId: null,
  productName: p.name,
  thumbnailUrl: p.thumbnailUrl ?? null,
  listPrice: p.salePrice,
  qty: 1,
  unitPrice: '',
});

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * ID55_2 상품선택 팝업. 상품명 검색(+페이지 더보기) → 결과 그리드에서 [추가] →
 * 하단 선택 리스트에서 수량/판매가(override) 편집. 최대 500건. 적용 시 부모로 전달.
 *
 * 고도몰 원안의 공급사구분·기간검색 컬럼은 catalog admin 상품목록 API 가 키워드/페이지만
 * 받으므로 상품명 검색 + 페이지네이션으로 우선 구현(공급사/기간은 BE 목록 필터 확장 시
 * 연결). unitPrice 미입력 행은 BE 가 catalog 판매가를 자동조회한다.
 */
export const ProductSelectModal = ({
  open,
  onOpenChange,
  initialSelection,
  onApply,
}: ProductSelectModalProps) => {
  const { t, i18n } = useTranslation('manualOrder');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<ProductSearchRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedProductRow[]>(initialSelection);

  // 모달이 열릴 때 선택 시작값으로 초기화하고 검색 결과를 리셋한다.
  useEffect(() => {
    if (open) {
      setSelected(initialSelection.map((r) => ({ ...r })));
      setKeywordDraft('');
      setKeyword('');
      setPage(0);
      setRows([]);
      setHasMore(false);
      setError(null);
    }
  }, [open, initialSelection]);

  // keyword/page 변경 시 검색. page 0 은 교체, 그 이상은 누적(더보기).
  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    searchProducts(keyword, page)
      .then((res) => {
        if (!active) return;
        setRows((prev) => (page === 0 ? res.rows : [...prev, ...res.rows]));
        setHasMore(res.hasMore);
      })
      .catch(() => {
        if (active) setError(t('manualOrder.productModal.loadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, keyword, page]);

  const selectedIds = useMemo(
    () => new Set(selected.map((s) => s.productId)),
    [selected],
  );

  const runSearch = () => {
    setKeyword(keywordDraft.trim());
    setPage(0);
  };

  const addRow = (p: ProductSearchRow) => {
    if (selectedIds.has(p.productId)) return;
    if (selected.length >= MANUAL_ORDER_MAX_LINES) {
      setError(t('manualOrder.productModal.maxReached', { max: MANUAL_ORDER_MAX_LINES }));
      return;
    }
    setSelected((prev) => [...prev, toSelectedRow(p)]);
  };

  const removeRow = (productId: string) => {
    setSelected((prev) => prev.filter((r) => r.productId !== productId));
  };

  const updateRow = (productId: string, patch: Partial<SelectedProductRow>) => {
    setSelected((prev) =>
      prev.map((r) => (r.productId === productId ? { ...r, ...patch } : r)),
    );
  };

  const handleApply = () => {
    onApply(selected.map((r) => ({ ...r })));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[840px]">
        <DialogHeader>
          <DialogTitle>{t('manualOrder.productModal.title')}</DialogTitle>
          <DialogDescription>
            {t('manualOrder.productModal.description', { max: MANUAL_ORDER_MAX_LINES })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {/* 검색 */}
          <div className="flex items-center gap-2">
            <Input
              value={keywordDraft}
              onChange={(e) => setKeywordDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder={t('manualOrder.productModal.searchPlaceholder')}
              data-testid="manual-order-product-search"
            />
            <Button size="sm" onClick={runSearch}>
              <Search size={14} aria-hidden="true" />
              {t('manualOrder.productModal.search')}
            </Button>
          </div>

          {error ? (
            <div
              className="rounded-md px-3 py-2 text-[12px]"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {/* 검색 결과 그리드 */}
          <div
            className="max-h-[220px] overflow-auto rounded-md"
            style={{ border: '1px solid var(--divider-soft)' }}
          >
            <table className="w-full border-collapse text-[12px]">
              <thead className="sticky top-0">
                <tr style={{ background: 'var(--surface-raised)' }}>
                  <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                    {t('manualOrder.productModal.colName')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium" style={{ color: 'var(--content-muted)' }}>
                    {t('manualOrder.productModal.colSalePrice')}
                  </th>
                  <th className="w-[64px] px-2 py-2" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={3} className="px-3 py-4 text-center" style={{ color: 'var(--content-muted)' }}>
                      {t('manualOrder.productModal.resultEmpty')}
                    </td>
                  </tr>
                ) : null}
                {rows.map((p) => (
                  <tr key={p.productId} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {p.thumbnailUrl ? (
                          <img
                            src={p.thumbnailUrl}
                            alt=""
                            className="h-7 w-7 rounded object-cover"
                            style={{ background: 'var(--surface-1)' }}
                          />
                        ) : null}
                        <div className="flex flex-col">
                          <span style={{ color: 'var(--content-strong)' }}>{p.name}</span>
                          {p.brandName ? (
                            <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                              {p.brandName}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--content-default)' }}>
                      {p.salePrice != null ? `${p.salePrice.toLocaleString(i18n.language)}원` : '-'}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedIds.has(p.productId)}
                        onClick={() => addRow(p)}
                        aria-label={t('manualOrder.productModal.addAria')}
                      >
                        <Plus size={13} aria-hidden="true" />
                        {selectedIds.has(p.productId) ? t('manualOrder.productModal.added') : t('manualOrder.productModal.add')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore ? (
            <div>
              <Button
                variant="ghost"
                size="sm"
                disabled={loading}
                onClick={() => setPage((p) => p + 1)}
              >
                {loading ? t('manualOrder.productModal.loadingMore') : t('manualOrder.productModal.loadMore')}
              </Button>
            </div>
          ) : null}

          {/* 선택 목록 */}
          <div>
            <p className="mb-1 text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
              {t('manualOrder.productModal.selectedTitle', { count: selected.length })}
            </p>
            <div
              className="max-h-[200px] overflow-auto rounded-md"
              style={{ border: '1px solid var(--divider-soft)' }}
            >
              <table className="w-full border-collapse text-[12px]">
                <thead className="sticky top-0">
                  <tr style={{ background: 'var(--surface-raised)' }}>
                    <th className="px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                      {t('manualOrder.productModal.colName')}
                    </th>
                    <th className="w-[88px] px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                      {t('manualOrder.productModal.colQty')}
                    </th>
                    <th className="w-[130px] px-3 py-2 text-left font-medium" style={{ color: 'var(--content-muted)' }}>
                      {t('manualOrder.productModal.colSalePriceAuto')}
                    </th>
                    <th className="w-[44px] px-2 py-2" aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {selected.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-center" style={{ color: 'var(--content-muted)' }}>
                        {t('manualOrder.productModal.selectedEmpty')}
                      </td>
                    </tr>
                  ) : null}
                  {selected.map((row) => (
                    <tr key={row.productId} style={{ borderTop: '1px solid var(--divider-soft)' }}>
                      <td className="px-3 py-1.5" style={{ color: 'var(--content-strong)' }}>
                        {row.productName}
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          min={1}
                          step="1"
                          value={String(row.qty)}
                          onChange={(e) =>
                            updateRow(row.productId, { qty: Math.max(1, num(e.target.value)) })
                          }
                          className="h-8 tabular-nums"
                          aria-label={t('manualOrder.productModal.qtyAria')}
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <Input
                          type="number"
                          min={0}
                          step="1"
                          value={row.unitPrice}
                          onChange={(e) => updateRow(row.productId, { unitPrice: e.target.value })}
                          placeholder={
                            row.listPrice != null
                              ? row.listPrice.toLocaleString(i18n.language)
                              : t('manualOrder.productModal.priceAuto')
                          }
                          className="h-8 tabular-nums"
                          aria-label={t('manualOrder.productModal.salePriceAria')}
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <button
                          type="button"
                          onClick={() => removeRow(row.productId)}
                          aria-label={t('manualOrder.productModal.remove')}
                          style={{ color: 'var(--signal-danger)', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t('manualOrder.productModal.cancel')}
          </Button>
          <Button size="sm" onClick={handleApply} data-testid="manual-order-product-apply">
            {t('manualOrder.productModal.apply', { count: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
